import type { FastifyRequest, FastifyReply } from 'fastify'
import { createLogger } from '../../common/utils/logger.js'
import type { ProfileService } from '../profile/profile.service.js'
import type { GamificationService } from '../gamification/gamification.service.js'
import type { QuestService } from '../gamification/quest.service.js'
import { authService } from '../auth/auth.service.js'

const logger = createLogger('DashboardController')

export interface DashboardInitResponse {
  profile: unknown
  gamification: unknown
  quests: unknown
}

export class DashboardController {
  constructor(
    private readonly profileService: ProfileService,
    private readonly gamificationService: GamificationService,
    private readonly questService: QuestService
  ) {}

  /**
   * Initialize dashboard: fetch ALL dashboard data in parallel
   * This is much faster than making 5 separate requests
   * GET /api/dashboard/init
   */
  async init(request: FastifyRequest, reply: FastifyReply) {
    const userId = request.user?.uid
    if (!userId) {
      return reply.code(401).send({ error: 'Unauthorized' })
    }

    try {
      logger.info('Dashboard init started', { userId })

      // Fetch ALL dashboard data in parallel
      const [profile, gamification, quests] = await Promise.all([
        this.profileService.getProfile(userId, { userAgent: request.headers['user-agent'] as string | undefined }).catch((e: Error) => {
          logger.warn('Failed to fetch profile', { userId, error: e.message })
          return null
        }),
        this.gamificationService.getUserProfile(userId).catch((e: Error) => {
          logger.warn('Failed to fetch gamification', { userId, error: e.message })
          return null
        }),
        this.questService.getActiveQuests(userId).catch((e: Error) => {
          logger.warn('Failed to fetch quests', { userId, error: e.message })
          return null
        })
      ])

      logger.info('Dashboard init completed', { userId })

      return reply.code(200).send({
        profile,
        gamification,
        quests
      })
    } catch (error) {
      logger.error('Dashboard init failed', { userId, error })
      return reply.code(500).send({ error: 'Failed to initialize dashboard' })
    }
  }
}
